import { useState, useEffect } from 'react';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet, apiPost } from '../utils/apiUtils';
import ParticleBackground from './ParticleBackground';
import { IS_EUROPE } from '../config/region';
import AuthorOfferModal from './AuthorOfferModal';
import './Building.css';

const getCoverImageUrl = (novel) => {
  if (novel.cover_background_image_id && novel.cover_background_image_extension) {
    return `${GCS_BUCKET_URL}/building/${novel.id}/background/${novel.cover_background_image_id}.${novel.cover_background_image_extension}`;
  }
  return null;
};

const Building = () => {
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);

  useEffect(() => {
    fetchNovels();
    checkOfferStatus();
  }, []);

  const checkOfferStatus = async () => {
    try {
      const response = await apiGet(API_URLS.USER_ME, {}, false, '', true);
      if (response.data.is_creator && !response.data.author_offer_accepted) {
        setShowOfferModal(true);
      }
    } catch (err) {
      // ignore — access errors are handled by fetchNovels
    }
  };

  const fetchNovels = async () => {
    try {
      setLoading(true);
      const response = await apiGet(API_URLS.BUILDING_NOVELS, {}, false, '', true);
      setNovels(response.data);
    } catch (err) {
      if (err.message && err.message.includes('Доступ к конструктору закрыт')) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleCreateNovel = async (event) => {
    event.preventDefault();
    setCreateError(null);
    setCreateLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name');

    if (!name || name.trim() === '') {
      setCreateError('Название новеллы обязательно');
      setCreateLoading(false);
      return;
    }

    try {
      await apiPost(
        API_URLS.BUILDING_NOVELS,
        name.trim(),
        { headers: { 'Content-Type': 'application/json' } },
        true,
        'Новелла создана успешно!'
      );

      setShowCreateForm(false);

      // Refresh novels list
      await fetchNovels();
    } catch (err) {
      // Error is already shown by toast notification
    } finally {
      setCreateLoading(false);
    }
  };

  /*
   * Russia only. The screen recruits authors into the constructor through a Yandex form, in
   * Russian, and the form does not accept European applicants — Plotex does not run the open
   * author intake that AI Novel does, which is why /authors is not routed here either. A visitor
   * without creator access gets an empty page rather than an invitation they cannot act on.
   */
  if (accessDenied) {
    if (IS_EUROPE) return null;
    return (
      <div className="bl-wrap">
        <ParticleBackground />
        <div className="bl-app">
          <div className="bl-header">
            <h1 className="bl-title">Присоединяйтесь к творческому сообществу авторов и зарабатывайте, создавая уникальные истории</h1>
            <p className="bl-subtitle">
              Конструктор новелл доступен только для авторизованных создателей.
            </p>
          </div>
          <div className="bl-empty">
            <p>Чтобы получить доступ к конструктору, заполните форму:</p>
            <a
              href="https://forms.yandex.ru/cloud/6a36840cd046882832495672/"
              target="_blank"
              rel="noopener noreferrer"
              className="bl-add-btn"
              style={{
                background: 'linear-gradient(135deg, #7B2FFF, #a78bfa)',
                color: '#fff',
                textDecoration: 'none',
                marginTop: '12px'
              }}
            >
              📝 Заполнить форму
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bl-wrap">
        <div className="bl-app">
          <div className="bl-loading">Загрузка...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bl-wrap">
      <ParticleBackground />
      {showOfferModal && (
        <AuthorOfferModal onAccepted={() => setShowOfferModal(false)} />
      )}
      <div className="bl-app">
        <div className="bl-header">
          <h1 className="bl-title">Мои создаваемые новеллы</h1>
          <p className="bl-subtitle">Создавайте и редактируйте интерактивные визуальные новеллы</p>
        </div>

        {/* Create error message */}
        {createError && (
          <div className="bl-create-error">{createError}</div>
        )}

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="bl-overlay">
            <div className="bl-modal">
              <h2 className="bl-modal-title">Создать новую новеллу</h2>
              <form onSubmit={handleCreateNovel}>
                <div className="bl-form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Название новеллы"
                    required
                    disabled={createLoading}
                    className="bl-input"
                  />
                </div>
                <div className="bl-modal-actions">
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="bl-btn-primary"
                  >
                    {createLoading ? 'Создание...' : 'Создать'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    disabled={createLoading}
                    className="bl-btn-cancel"
                  >
                    Отменить
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bl-toolbar">
          <button
            className="bl-add-btn"
            onClick={() => setShowCreateForm(true)}
          >
            <span>➕</span>
            Создать новую новеллу
          </button>
        </div>

        {novels.length > 0 ? (
          <div className="bl-grid">
            {novels.map((novel) => {
              const coverUrl = getCoverImageUrl(novel);
              return (
              <a
                key={novel.id}
                href={`/building/${novel.id}`}
                className="bl-card"
              >
                <div className="bl-card-cover">
                  {coverUrl ? (
                    <>
                      <img
                        src={coverUrl}
                        alt={novel.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="bl-card-placeholder" style={{ display: 'none' }}>🔨</div>
                    </>
                  ) : (
                    <div className="bl-card-placeholder">🔨</div>
                  )}
                </div>
                <div className="bl-card-body">
                  <h3 className="bl-card-name">{novel.name}</h3>
                  <p className="bl-card-desc">{novel.description || 'Описание отсутствует'}</p>
                  <span className="bl-card-edit">Редактировать новеллу</span>
                </div>
              </a>
              );
            })}
          </div>
        ) : (
          <div className="bl-empty">
            <p>Новелл не найдено. Начните создавать свою первую новеллу!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Building;
